"use client";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Users,
  ChevronDown,
  FolderKanban,
  CheckCircle2,
  Download,
  Loader2,
  CalendarCheck,
  CalendarX,
  Plane,
  Bug,
  Clock4,
  Gauge,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { useWork } from "../../context/WorkContext";
import { useTaskContext } from "../../context/TaskContext";
import { useAttendanceReport } from "../../context/AttendanceReportContext";
import { useLeaves } from "../../context/LeavesContext";
import { useTaskBugs } from "../../context/TaskBugsContext";
import { useAuth } from "../../context/AuthContext";
import { useEmployee } from "../../context/EmployeeContext";
import { CDN_URL } from "../../config/apiConfig";
import PerformanceDashboard from "./PerformanceDashboard";

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <div
      className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
      style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
    >
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 leading-tight">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const MetricCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0">
    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${accent} shrink-0`}>
      <Icon size={16} className="text-white sm:hidden" />
      <Icon size={18} className="text-white hidden sm:block" />
    </div>
    <div className="min-w-0">
      <p className="text-base sm:text-xl font-bold text-gray-800 leading-tight">{value}</p>
      <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  </div>
);

// Helper: safe string coercion for ID comparisons (handles number vs string mismatches
// coming from different API endpoints, e.g. user_id: "3" vs employee.id: 3)
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const Work = () => {
  const { employees, fetchAllEmployeesDashboard } = useWork();
  const { tasks: allTasks } = useTaskContext();
  const { reports: attendanceReports } = useAttendanceReport();
  const { leaves } = useLeaves();
  const { bugs } = useTaskBugs();
  const { user } = useAuth();
  const { employees: employeeDirectory } = useEmployee();
  const userRole = (user?.role || "").toLowerCase();
  const isAdminOrHr = userRole === "admin" || userRole === "hr";
  const isManager = userRole === "manager";

  // Sub-employees reporting to the logged-in manager, resolved from the
  // employee directory (which carries reporting_manager) since the
  // work-dashboard employee list doesn't include that field.
  const teamMemberIds = useMemo(() => {
    if (!isManager) return new Set();
    return new Set(
      (employeeDirectory || [])
        .filter((emp) => sameId(emp.reporting_manager, user?.id))
        .map((emp) => String(emp.empId))
    );
  }, [employeeDirectory, isManager, user]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [teamDropdown, setTeamDropdown] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);


const dropdownRef = useRef(null); // 👈 add this

  useEffect(() => {
    fetchAllEmployeesDashboard();
  }, [fetchAllEmployeesDashboard]);

useEffect(() => {
  if (!teamDropdown) return;

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setTeamDropdown(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  document.addEventListener("touchstart", handleClickOutside); // for mobile taps

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("touchstart", handleClickOutside);
  };
}, [teamDropdown]);
  // Only include employees whose status is Active (or unspecified).
  // Non-admin/HR users (e.g. a manager or employee granted this page) may only
  // view their own performance plus, for managers, their direct reports —
  // never the whole company's.
  const activeEmployees = useMemo(() => {
    const active = (employees || []).filter((emp) => !emp.status || emp.status === "Active");
    if (isAdminOrHr) return active;
    return active.filter(
      (emp) => sameId(emp.id, user?.id) || (isManager && teamMemberIds.has(String(emp.id)))
    );
  }, [employees, isAdminOrHr, isManager, teamMemberIds, user]);

  // Only worth showing the dropdown when there's actually more than one
  // employee to pick from.
  const canSwitchEmployee = isAdminOrHr || activeEmployees.length > 1;

  useEffect(() => {
    if (!selectedEmployee && activeEmployees.length > 0) {
      setSelectedEmployee(activeEmployees[0]); // first active employee
    }
  }, [activeEmployees, selectedEmployee]);

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "";
    const f = firstName ? firstName[0].toUpperCase() : "";
    const l = lastName ? lastName[0].toUpperCase() : "";
    return f + l;
  };

  // ── Tasks (TaskContext) — same source as the Task/Kanban page ───────────
  const employeeTasks = useMemo(() => {
    if (!selectedEmployee) return [];
    return (allTasks || []).filter((t) => sameId(t.user_id, selectedEmployee.id));
  }, [allTasks, selectedEmployee]);
useEffect(() => {
  const withHistory = employeeTasks.find(t => (t.progress_history || []).length > 0);
  if (withHistory) {
    console.log("assigned_hours:", withHistory.assigned_hours, "time:", withHistory.time);
    console.log("progress_history entry:", JSON.stringify(withHistory.progress_history[0], null, 2));
  } else {
    console.log("still no task with progress_history entries");
  }

  // ADD THIS:
  if (employeeTasks[0]) {
    console.log("sample task keys:", Object.keys(employeeTasks[0]));
    console.log("sample task full:", JSON.stringify(employeeTasks[0], null, 2));
  }
}, [employeeTasks]);
  // ── Attendance (AttendanceReportContext) ────────────────────────────────
  const employeeAttendance = useMemo(() => {
    if (!selectedEmployee) return null;
    const fullName = `${selectedEmployee.first_name} ${selectedEmployee.last_name}`;
    return (
      (attendanceReports || []).find(
        (a) => sameId(a.user_id, selectedEmployee.id) || a.employee_name === fullName
      ) || null
    );
  }, [attendanceReports, selectedEmployee]);

  const attendanceStats = useMemo(() => {
    const months = employeeAttendance?.months || [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1–12

    const yearMonths = months.filter((m) => {
      if (!m.month) return false;
      const [y, mo] = m.month.split("-").map(Number);
      return y === currentYear && mo <= currentMonth; // only Jan → current month
    });

    const present = yearMonths.reduce((sum, m) => sum + (m.presents_count || 0), 0);
    const halfDay = yearMonths.reduce((sum, m) => sum + (m.half_days_count || 0), 0);
    const absent = yearMonths.reduce((sum, m) => sum + (m.absents_count || 0), 0);
    const lateMinutes = yearMonths.reduce((sum, m) => sum + (m.late_minutes || 0), 0);
    const total = present + halfDay + absent;
    const rate = total ? Math.round(((present + halfDay * 0.5) / total) * 100) : 0;

    return { present, halfDay, absent, lateMinutes, total, rate, year: currentYear.toString() };
  }, [employeeAttendance]);

  // ── Leaves (LeavesContext) ──────────────────────────────────────────────
  const employeeLeaves = useMemo(() => {
    if (!selectedEmployee) return [];

    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1); // Jan 1 of current year
    const today = now;

    return (leaves || [])
      .filter((l) => {
        const belongsToEmployee =
          sameId(l.user_id, selectedEmployee.id) ||
          (l.first_name === selectedEmployee.first_name && l.last_name === selectedEmployee.last_name);
        if (!belongsToEmployee) return false;

        if (!l.start_date || l.start_date === "0000-00-00") return false; // exclude invalid/placeholder dates
        const leaveStart = new Date(l.start_date);
        if (isNaN(leaveStart.getTime())) return false;

        return leaveStart >= startOfYear && leaveStart <= today;
      })
      // Most recent first, so the "recent leaves" list (sliced below) actually shows recent leaves
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  }, [leaves, selectedEmployee]);

  useEffect(() => {
  console.log("employeeLeaves (sorted):", employeeLeaves.map(l => `${l.start_date} [${l.status}]`));
}, [employeeLeaves]);
  const leaveStats = useMemo(() => {
    // Treat any "approved-ish" status (approved / manager_approved / etc.) as approved
    const isApprovedStatus = (status) => (status || "").toLowerCase().includes("approved");
    const isRejectedStatus = (status) => (status || "").toLowerCase().includes("reject");
    const isPendingStatus = (status) => (status || "").toLowerCase().includes("pending");

    const approved = employeeLeaves.filter((l) => isApprovedStatus(l.status));
    const pending = employeeLeaves.filter((l) => isPendingStatus(l.status)).length;
    const rejected = employeeLeaves.filter((l) => isRejectedStatus(l.status)).length;
    const approvedDays = approved.reduce((sum, l) => sum + (parseFloat(l.number_of_days) || 0), 0);
    return { total: employeeLeaves.length, approvedCount: approved.length, pending, rejected, approvedDays };
  }, [employeeLeaves]);

  // ── Bugs & Change Requests (TaskBugsContext) ────────────────────────────
  // Linked to the employee's tasks via bug.task_id === task.task_id.
  // bug.type === "CR" -> new dev request (change request); otherwise -> bug.
  const isResolvedBugStatus = (status) =>
    ["fixed", "closed", "done", "resolved"].includes((status || "").toLowerCase());

  // A bug/CR is "reopened" if its status history ever shows a transition back to Reopened.
  const hasBeenReopened = (b) =>
    Array.isArray(b.status_history) &&
    b.status_history.some((h) => (h.to_status || "").toLowerCase() === "reopened");

  const employeeBugs = useMemo(() => {
    const taskIdSet = new Set(
      employeeTasks.flatMap((t) => [t.task_id, t.id, t.tid].filter((v) => v != null).map(String))
    );

    return (bugs || []).filter((b) => {
      const linkedByTask =
        (b.task_id != null && taskIdSet.has(String(b.task_id))) ||
        (b.tid != null && taskIdSet.has(String(b.tid)));
      const linkedByPerson =
        sameId(b.tester_id, selectedEmployee?.id) ||
        b.employee_name === `${selectedEmployee?.first_name} ${selectedEmployee?.last_name}`;
      return linkedByTask || linkedByPerson;
    });
  }, [bugs, employeeTasks, selectedEmployee]);

  const normalizeType = (t) => (t || "").toString().trim().toUpperCase();

  const taskTypeStats = useMemo(() => {
    const bugsList = employeeBugs.filter((b) => normalizeType(b.type) !== "CR");
    const crsList = employeeBugs.filter((b) => normalizeType(b.type) === "CR");
    const bugsResolved = bugsList.filter((b) => isResolvedBugStatus(b.status)).length;
    const crsResolved = crsList.filter((b) => isResolvedBugStatus(b.status)).length;
    const bugsReopened = bugsList.filter(hasBeenReopened).length;
    const crsReopened = crsList.filter(hasBeenReopened).length;
    return {
      bugs: { total: bugsList.length, resolved: bugsResolved, open: bugsList.length - bugsResolved, reopened: bugsReopened },
      crs: { total: crsList.length, resolved: crsResolved, open: crsList.length - crsResolved, reopened: crsReopened },
    };
  }, [employeeBugs]);

  // ── Project-level breakdown — grouped from the employee's own tasks/bugs ─
  const projectReport = useMemo(() => {
    const map = new Map();

    const getEntry = (key, t) => {
      if (!map.has(key)) {
        map.set(key, {
          projectId: t.project_id,
          projectName: t.project_name || "Unassigned",
          projectCode: t.project_code,
          total: 0,
          completed: 0,
          inProgress: 0,
          points: 0,
          bugs: 0,
          bugsOpen: 0,
          crs: 0,
          crsOpen: 0,
        });
      }
      return map.get(key);
    };

    employeeTasks.forEach((t) => {
      const key = t.project_id ?? t.project_name ?? "unassigned";
      const entry = getEntry(key, t);
      entry.total += 1;
      if (t.status === "Completed") entry.completed += 1;
      if (t.status === "Inprogress") entry.inProgress += 1;
      entry.points += parseFloat(t.points) || 0;
    });

    employeeBugs.forEach((b) => {
      const key = b.project_id ?? b.project_name ?? "unassigned";
      const entry = getEntry(key, { project_id: b.project_id, project_name: b.project_name, project_code: b.project_code });
      const resolved = isResolvedBugStatus(b.status);

      if (normalizeType(b.type) === "CR") {
        entry.crs += 1;
        if (!resolved) entry.crsOpen += 1;
      } else {
        entry.bugs += 1;
        if (!resolved) entry.bugsOpen += 1;
      }
    });

    return Array.from(map.values())
      .map((p) => ({ ...p, completionRate: p.total ? Math.round((p.completed / p.total) * 100) : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [employeeTasks, employeeBugs]);

  // Temporarily swap cross-origin <img> sources (e.g. the employee's CDN photo)
  // for a pre-fetched base64 copy so html2canvas can draw them without hitting
  // canvas-tainting/CORS issues. This only ever changes `src` to a data URL —
  // never the element's `crossOrigin` flag, which would force the browser to
  // re-fetch the *visible* image under CORS and could break it on screen if
  // that specific file isn't served with CORS headers. Returns a restore fn.
  const swapImagesToDataUrls = async (container) => {
    const images = Array.from(container.querySelectorAll("img"));
    const restores = [];

    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            const originalSrc = img.src;
            if (!originalSrc || originalSrc.startsWith("data:")) {
              resolve();
              return;
            }

            // This same URL is already displayed elsewhere on the page (header,
            // dropdown) via a plain <img> with no crossOrigin. Re-requesting the
            // exact same URL here with crossOrigin="anonymous" can collide with
            // that cached non-CORS response and fail to load at all — so a cache
            // -busting param forces a fresh, independent, CORS-mode fetch.
            const probe = new Image();
            probe.crossOrigin = "anonymous";
            probe.onload = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = probe.naturalWidth;
                canvas.height = probe.naturalHeight;
                canvas.getContext("2d").drawImage(probe, 0, 0);
                const dataUrl = canvas.toDataURL("image/png");
                restores.push(() => {
                  img.src = originalSrc;
                });
                img.src = dataUrl;
              } catch {
                // Tainted/unreadable — leave the original src; html2canvas
                // will just skip drawing this particular image.
              }
              resolve();
            };
            probe.onerror = () => resolve(); // leave original src in place
            probe.src = originalSrc + (originalSrc.includes("?") ? "&" : "?") + "pdfcapture=" + Date.now();
          })
      )
    );

    // Let the browser apply/paint the swapped sources before we measure/capture.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    return () => restores.forEach((restore) => restore());
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current || !selectedEmployee) return;
    const employeeForReport = selectedEmployee; // freeze in case the selection changes mid-generation
    setDownloading(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const container = reportRef.current;
      const restoreImages = await swapImagesToDataUrls(container);

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;

      // Capture each top-level section (card) as its own separate image and
      // place it as a whole block, starting a new page whenever a section
      // wouldn't fully fit. This is what actually guarantees a heading/card
      // can never be split or bleed across a page break — html2canvas renders
      // from an internal clone of the page, so positions measured on the live
      // DOM beforehand don't reliably line up with where content really lands
      // in a single big screenshot; capturing each section on its own avoids
      // needing that cross-reference at all.
      let cursorY = 0;
      let firstPage = true;

      try {
        for (const section of Array.from(container.children)) {
          const canvas = await html2canvas(section, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#f0f9ff",
            // Draw text glyph-by-glyph instead of line-by-line — avoids the
            // gaps/lines html2canvas's default text renderer can leave
            // through bold text, without changing the rendering pipeline.
            letterRendering: true,
          });

          const imgData = canvas.toDataURL("image/png");
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          if (!firstPage && cursorY + imgHeight > pageHeight) {
            pdf.addPage();
            cursorY = 0;
          }
          firstPage = false;

          if (imgHeight > pageHeight) {
            // This one section alone is taller than a full page — fall back
            // to slicing just this section across as many pages as it needs.
            let heightLeft = imgHeight;
            let pos = cursorY;
            pdf.addImage(imgData, "PNG", 0, pos, imgWidth, imgHeight);
            heightLeft -= pageHeight - cursorY;
            while (heightLeft > 0) {
              pdf.addPage();
              pos = -(imgHeight - heightLeft);
              pdf.addImage(imgData, "PNG", 0, pos, imgWidth, imgHeight);
              heightLeft -= pageHeight;
            }
            cursorY = pageHeight; // force the next section onto a fresh page
          } else {
            pdf.addImage(imgData, "PNG", 0, cursorY, imgWidth, imgHeight);
            cursorY += imgHeight;
          }
        }
      } finally {
        restoreImages();
      }

      const fileName = `${employeeForReport.first_name}_${employeeForReport.last_name}_Performance_Report.pdf`.replace(/\s+/g, "_");
      pdf.save(fileName);
    } catch (err) {
      console.error("Failed to generate PDF", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-sky-50 min-h-screen p-3 sm:p-8 overflow-x-hidden">
      {/* Header banner */}
      <div
        className="rounded-2xl p-4 sm:p-6 mb-6 text-white relative overflow-visible"
        style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
      >
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {selectedEmployee?.image ? (
              <img
                src={`${CDN_URL}/${selectedEmployee.image}`}
                alt={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl object-cover ring-2 ring-white/30 shrink-0"
              />
            ) : (
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/15 flex items-center justify-center text-lg font-semibold shrink-0">
                {selectedEmployee ? getInitials(selectedEmployee.first_name, selectedEmployee.last_name) : <Users size={24} />}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate">
                {selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}` : "Employee Performance"}
              </h1>
              <p className="text-xs sm:text-sm text-white/80 mt-0.5 truncate flex items-center gap-2">
                <span className="truncate">
                  {selectedEmployee?.department_name || "Track performance, workload and project progress per employee."}
                </span>
                {selectedEmployee?.status && (
                  <span className="shrink-0 inline-flex items-center text-[11px] font-semibold text-white bg-white/20 px-2 py-0.5 rounded-full">
                    {selectedEmployee.status}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:justify-end">
            {/* Employee selector — admins/HR can switch between everyone, managers
                between themselves and their direct reports; a plain employee only
                ever has themselves in activeEmployees, so the dropdown is hidden. */}
      <div className="relative min-w-0" ref={dropdownRef}>
  <button
    onClick={() => canSwitchEmployee && setTeamDropdown(!teamDropdown)}
    disabled={downloading || !canSwitchEmployee}
    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/15 hover:bg-white/25 transition max-w-full disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <span className="text-white/80 shrink-0 hidden xs:inline">View:</span>
    {selectedEmployee?.image ? (
      <img
        src={`${CDN_URL}/${selectedEmployee.image}`}
        alt={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
        className="w-6 h-6 rounded-full object-cover shrink-0"
      />
    ) : selectedEmployee ? (
      <div className="w-6 h-6 rounded-full flex items-center justify-center bg-white/20 text-white text-[10px] font-semibold shrink-0">
        {getInitials(selectedEmployee.first_name, selectedEmployee.last_name)}
      </div>
    ) : null}
    <span className="font-medium truncate max-w-[90px] sm:max-w-[160px]">
      {selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}` : "Select Employee"}
    </span>
    {canSwitchEmployee && <ChevronDown size={16} className="shrink-0" />}
  </button>

  {teamDropdown && (
    <div
      className="absolute mt-2 w-56 max-w-[85vw] bg-white shadow-lg rounded-lg border z-50 max-h-64 overflow-y-auto text-gray-700"
      style={{ right: "-18px" }}
    >
      {activeEmployees.length === 0 ? (
        <p className="px-3 py-2 text-sm text-gray-500">No active employees found</p>
      ) : (
        activeEmployees.map((emp) => (
          <button
            key={emp.id}
            onClick={() => {
              setSelectedEmployee(emp);
              setTeamDropdown(false);
            }}
            className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
              selectedEmployee?.id === emp.id ? "bg-blue-50 font-medium text-blue-700" : "text-gray-700"
            }`}
          >
            {emp.image ? (
              <img
                src={`${CDN_URL}/${emp.image}`}
                alt={`${emp.first_name} ${emp.last_name}`}
                className="w-6 h-6 rounded-full object-cover shrink-0"
              />
            ) : (
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                {getInitials(emp.first_name, emp.last_name)}
              </div>
            )}
            <span className="truncate">{emp.first_name} {emp.last_name}</span>
          </button>
        ))
      )}
    </div>
  )}
</div>

            {/* Download PDF */}
            <button
              onClick={handleDownloadPdf}
              disabled={!selectedEmployee || downloading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/15 hover:bg-white/25 transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              <span className="font-medium hidden xs:inline">{downloading ? "Generating..." : "Download PDF"}</span>
              <span className="font-medium xs:hidden">{downloading ? "..." : "PDF"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Single-page report */}
      <div ref={reportRef} className="space-y-6 bg-sky-50">
        {/* Performance Dashboard */}
        <PerformanceDashboard
          tasks={employeeTasks}
          employee={selectedEmployee}
          title={
            selectedEmployee
              ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}'s Performance`
              : "Performance Dashboard"
          }
        />

        {/* Attendance Overview */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl shadow overflow-hidden">
          <SectionHeader
            icon={CalendarCheck}
            title="Attendance Overview"
            subtitle={employeeAttendance ? `${attendanceStats.total} working day(s) in ${attendanceStats.year}` : "No attendance records found"}
          />
          {employeeAttendance ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                <MetricCard icon={CalendarCheck} label="Present Days" value={attendanceStats.present} accent="bg-gradient-to-br from-green-500 to-emerald-600" />
                <MetricCard icon={Clock4} label="Half Days" value={attendanceStats.halfDay} accent="bg-gradient-to-br from-amber-400 to-orange-500" />
                <MetricCard icon={CalendarX} label="Absent Days" value={attendanceStats.absent} accent="bg-gradient-to-br from-red-500 to-rose-600" />
                <MetricCard icon={AlertTriangle} label="Late Minutes" value={attendanceStats.lateMinutes} accent="bg-gradient-to-br from-orange-500 to-red-500" />
                <MetricCard icon={Gauge} label="Attendance Rate" value={`${attendanceStats.rate}%`} accent="bg-gradient-to-br from-blue-500 to-indigo-600" />
              </div>
              <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${attendanceStats.rate}%`, background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                />
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-sm">No attendance records found for this employee.</p>
          )}
        </div>

        {/* Leave Summary */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl shadow overflow-hidden">
          <SectionHeader
            icon={Plane}
            title="Leave Summary"
            subtitle={`${leaveStats.total} request${leaveStats.total === 1 ? "" : "s"} total`}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <MetricCard icon={CalendarCheck} label="Approved" value={leaveStats.approvedCount} accent="bg-gradient-to-br from-green-500 to-emerald-600" />
            <MetricCard icon={Clock4} label="Pending" value={leaveStats.pending} accent="bg-gradient-to-br from-amber-400 to-orange-500" />
            <MetricCard icon={CalendarX} label="Rejected" value={leaveStats.rejected} accent="bg-gradient-to-br from-red-500 to-rose-600" />
            <MetricCard icon={Plane} label="Days Taken" value={leaveStats.approvedDays} accent="bg-gradient-to-br from-violet-500 to-purple-600" />
          </div>

          {employeeLeaves.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {employeeLeaves.slice(0, 6).map((l) => (
                <div key={l.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3 text-sm border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/50">
                  <div className="min-w-0 flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-gray-700">{l.leave_type || "Leave"}</span>
                    <span className="text-gray-500 text-xs sm:text-sm break-words">
                      {l.start_date} → {l.end_date}
                    </span>
                  </div>
                  <span
                    className={`self-start sm:self-auto shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      (l.status || "").toLowerCase().includes("approved")
                        ? "text-green-600 bg-green-50"
                        : (l.status || "").toLowerCase().includes("reject")
                        ? "text-red-600 bg-red-50"
                        : "text-amber-600 bg-amber-50"
                    }`}
                  >
                    {l.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No leave requests found.</p>
          )}
        </div>

        {/* Project-wise Performance */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl shadow overflow-hidden">
          <SectionHeader
            icon={FolderKanban}
            title="Project-wise Performance"
            subtitle={`${projectReport.length} project${projectReport.length === 1 ? "" : "s"} with assigned tasks`}
          />
          {projectReport.length === 0 ? (
            <p className="text-gray-500 text-sm">No project-linked tasks found.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projectReport.map((p) => (
                <div key={p.projectId ?? p.projectName} className="border border-gray-100 rounded-xl p-4 bg-gray-50/50 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                    <h3 className="font-semibold text-gray-800 min-w-0">{p.projectName}</h3>
                    {p.projectCode && (
                      <span className="shrink-0 text-[11px] font-semibold text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                        {p.projectCode}
                      </span>
                    )}
                  </div>

                  <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden mb-3">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${p.completionRate}%`, background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                    />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-bold text-gray-800">{p.total}</p>
                      <p className="text-[11px] text-gray-500">Tasks</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-bold text-emerald-600">{p.completed}</p>
                      <p className="text-[11px] text-gray-500">Completed</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-bold text-violet-600">{p.points}</p>
                      <p className="text-[11px] text-gray-500">Points</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-bold text-rose-600">{p.bugsOpen}/{p.bugs}</p>
                      <p className="text-[11px] text-gray-500">Open Bugs</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-bold text-blue-600">{p.crsOpen}/{p.crs}</p>
                      <p className="text-[11px] text-gray-500">Open CRs</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bugs & Change Requests */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl shadow overflow-hidden">
          <SectionHeader
            icon={Bug}
            title="Bugs & Change Requests"
            subtitle={`${taskTypeStats.bugs.total} bug${taskTypeStats.bugs.total === 1 ? "" : "s"}, ${taskTypeStats.crs.total} new dev request${taskTypeStats.crs.total === 1 ? "" : "s"} linked to assigned tasks`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Bug size={16} className="text-rose-500 shrink-0" /> Bugs
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <MetricCard icon={Bug} label="Total" value={taskTypeStats.bugs.total} accent="bg-gradient-to-br from-blue-500 to-indigo-600" />
                <MetricCard icon={CheckCircle2} label="Resolved" value={taskTypeStats.bugs.resolved} accent="bg-gradient-to-br from-green-500 to-emerald-600" />
                <MetricCard icon={AlertTriangle} label="Open" value={taskTypeStats.bugs.open} accent="bg-gradient-to-br from-red-500 to-rose-600" />
                <MetricCard icon={RotateCcw} label="Reopened" value={taskTypeStats.bugs.reopened} accent="bg-gradient-to-br from-purple-500 to-fuchsia-600" />
              </div>
            </div>

            <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FolderKanban size={16} className="text-violet-500 shrink-0" /> New Dev Requests (CR)
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <MetricCard icon={FolderKanban} label="Total" value={taskTypeStats.crs.total} accent="bg-gradient-to-br from-blue-500 to-indigo-600" />
                <MetricCard icon={CheckCircle2} label="Resolved" value={taskTypeStats.crs.resolved} accent="bg-gradient-to-br from-green-500 to-emerald-600" />
                <MetricCard icon={AlertTriangle} label="Open" value={taskTypeStats.crs.open} accent="bg-gradient-to-br from-red-500 to-rose-600" />
                <MetricCard icon={RotateCcw} label="Reopened" value={taskTypeStats.crs.reopened} accent="bg-gradient-to-br from-purple-500 to-fuchsia-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Work;